import { MigrationFunction } from '@framework/engine/migrations/MigrationFunction.ts';
import { type IRootWidget } from '@framework/engine/schema/widgets/repository/containers/root/IRootWidget.ts';
import { TreeNavigatorLogic } from '@framework/engine/tree/navigation/TreeNavigatorLogic.ts';
import { getUniqueCode } from '../../../../utils/getUniqueCode.ts';
import { CADASTRAL_LAYER_CODE } from '../config/cadastral/cadastralPreset.ts';
import {
  type IMapWidget,
  MapWidgetSelectionShape,
  WidgetTypeMap,
} from '../IMapWidget.ts';
import { getLayerCodeBase } from '../layers/assignUniqueLayerCode.ts';
import {
  type IMapLayer,
  type IMapLayerCompetence,
  type IMapLayerIntersection,
} from '../layers/MapLayer.ts';
import { SYNTHETIC_LAYER_STYLE } from '../mapStyleUtils.ts';

/**
 * Attributi catastali estratti dalle feature ArcGIS.
 * Corrispondono ai campi del servizio cartografico catastale
 * utilizzati dal preset catastale (MapIntersectionLayerSection).
 */
const CADASTRAL_ATTRIBUTES = ['SEZIONE', 'NUMERO', 'COMUNE', 'FOGLIO'];

/**
 * Porta le sezioni legacy (versionId 0) al modello unificato dei layer mappa.
 *
 * Per ogni widget mappa, in ordine:
 * 1. `selection.shape` (singolo) → `selection.shapes` (array).
 * 2. Intersezione widget-level → layer (trova per url+type o crea sintetico
 *    con `cadastralPreset`).
 * 3. Competenza widget-level → layer (trova per url+type o crea sintetico
 *    con ruolo primario).
 * 4. Assegna `layer.code` a tutti i layer (identificatore stabile e univoco,
 *    derivato dal nome e deduplicato).
 *
 */
export class MapWidgetMigration001UnifiedLayerModel extends MigrationFunction<IRootWidget> {
  readonly versionId = 1;

  run(data: IRootWidget): void {
    TreeNavigatorLogic.forEachNode(data, node => {
      if ((node.type as string) === WidgetTypeMap) {
        this.migrateMapWidget(node as any);
      }
    });
  }

  private migrateMapWidget(widget: IMapWidget): void {
    this.migrateSelectionShape(widget);
    this.migrateIntersection(widget);
    this.migrateCompetence(widget);
    this.assignLayerCodes(widget);
  }

  /**
   * Normalizza `selection.shapes` in un array di {@link MapWidgetSelectionShape}
   * e migra il vecchio campo singolo `selection.shape`.
   *
   * Gestisce due formati legacy:
   * - `shape` singolo → `[shape]`;
   * - `shapes` come mappa `{ polygon: true, point: false, ... }` (vecchia UI a
   *   checkbox) → array delle sole chiavi attive. Senza questa conversione il
   *   runtime, che si aspetta un array (`shapes.includes`, `for..of`), va in
   *   errore su quei widget migrati.
   */
  private migrateSelectionShape(widget: IMapWidget): void {
    const selection = widget.selection;
    if (!selection) return;

    const validShapes = new Set<MapWidgetSelectionShape>(
      Object.values(MapWidgetSelectionShape),
    );
    const rawShapes: unknown = selection.shapes;

    let shapes: MapWidgetSelectionShape[];
    if (Array.isArray(rawShapes)) {
      // Array già presente: rimuove valori non validi e deduplica.
      shapes = [...new Set(rawShapes)].filter(
        (shape): shape is MapWidgetSelectionShape =>
          validShapes.has(shape as MapWidgetSelectionShape),
      );
    } else if (rawShapes && typeof rawShapes === 'object') {
      // Formato legacy a mappa → array delle sole geometrie attive.
      const shapeMap = rawShapes as Record<string, unknown>;
      shapes = [...validShapes].filter(shape => shapeMap[shape]);
      this.log(
        'selection.shapes in formato legacy (oggetto) convertito in array',
        rawShapes,
      );
    } else {
      shapes = [];
    }

    // Vecchio campo singolo `shape` → array, se `shapes` non è valorizzato.
    if (shapes.length === 0 && selection.shape) {
      shapes = [selection.shape];
    }

    // Non forzare un array vuoto: lasciando `shapes` non valorizzato il
    // runtime applica il default (`[Polygon]`), evitando un widget senza
    // geometrie selezionabili.
    if (shapes.length > 0) {
      selection.shapes = shapes;
    } else {
      delete selection.shapes;
    }
    delete selection.shape;
  }

  /** Intersezione widget-level → layer-level. */
  private migrateIntersection(widget: IMapWidget): void {
    const intersection = widget.intersection;

    if (!intersection?.enabled) {
      delete widget.intersection;
      return;
    }

    if (!intersection.type || !intersection.url) {
      this.log(
        'Intersection abilitata ma type o url mancanti, skip creazione layer',
      );
      delete widget.intersection;
      return;
    }

    const layerIntersection: IMapLayerIntersection = {
      enabled: true,
      title: intersection.title,
      attributes: CADASTRAL_ATTRIBUTES,
      areaTolerance: intersection.areaTolerance,
      cadastralPreset: true,
    };

    widget.layers ??= [];
    const matchingLayer = widget.layers.find(
      layer =>
        layer.url === intersection.url && layer.type === intersection.type,
    );

    if (matchingLayer) {
      matchingLayer.intersection = layerIntersection;
    } else {
      widget.layers.push({
        type: intersection.type,
        code: CADASTRAL_LAYER_CODE,
        name: intersection.title ?? 'Intersezioni',
        url: intersection.url,
        style: SYNTHETIC_LAYER_STYLE,
        intersection: layerIntersection,
      });
    }

    delete widget.intersection;
  }

  /** Competenza widget-level → layer-level. */
  private migrateCompetence(widget: IMapWidget): void {
    const competence = widget.competence;

    if (!competence?.enabled) {
      delete widget.competence;
      return;
    }

    if (!competence.type || !competence.url || !competence.propertyName) {
      this.log(
        'Competenza abilitata ma type, url o propertyName mancanti, skip creazione layer',
      );
      delete widget.competence;
      return;
    }

    const layerCompetence: IMapLayerCompetence = {
      enabled: true,
      role: 'primary',
      identifyingPropertyName: competence.propertyName,
    };

    widget.layers ??= [];
    const matchingLayer = widget.layers.find(
      layer => layer.url === competence.url && layer.type === competence.type,
    );

    if (matchingLayer) {
      matchingLayer.competence = layerCompetence;
    } else {
      widget.layers.push({
        type: competence.type,
        code: 'competenza',
        name: 'Competenza',
        url: competence.url,
        style: SYNTHETIC_LAYER_STYLE,
        competence: layerCompetence,
      });
    }

    delete widget.competence;
  }

  /**
   * Assegna un `code` stabile e univoco a tutti i layer del widget: sia quelli
   * principali sia i layer di sfondo delle print image, che hanno la stessa
   * shape legacy e sono ora validati con `MapLayerSchema` (code obbligatorio).
   */
  private assignLayerCodes(widget: IMapWidget): void {
    this.assignCodesToLayers(widget.layers);
    for (const printImage of widget.printImages ?? []) {
      this.assignCodesToLayers(printImage.layers);
    }
  }

  /**
   * Deriva un `code` dal nome (o dal code esistente) per ogni layer dell'array,
   * deduplicandolo con suffisso sequenziale nell'ambito dell'array stesso.
   */
  private assignCodesToLayers(layers: IMapLayer[] | undefined): void {
    if (!layers?.length) return;

    const usedCodes = new Set<string>();
    for (const layer of layers) {
      const code = getUniqueCode(getLayerCodeBase(layer), usedCodes);
      usedCodes.add(code);
      layer.code = code;
    }
  }
}
